import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Clock, ChevronLeft, ChevronRight, MapPin, Coffee, UtensilsCrossed, Sandwich, ShoppingBag, Wine } from "lucide-react";

/**
 * PopularNowRail
 *
 * Time-of-day-aware hot rail. Pulls `/businesses/popular-now` which auto-picks
 * a slot (morning / lunch / afternoon / dinner / evening) based on the
 * caller's timezone offset and category-filters the pool accordingly.
 *
 * Silently hidden when the slot returns zero listings — quiet regions get
 * the standard directory feed without an empty rail.
 */
const SLOT_ICONS = {
  morning: Coffee,
  lunch: Sandwich,
  afternoon: ShoppingBag,
  dinner: UtensilsCrossed,
  evening: Wine,
};

const CARD_WIDTH = 240;

export default function PopularNowRail() {
  const [state, setState] = useState({ items: [], slot: null, loading: true });
  const railRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const tz_offset = -new Date().getTimezoneOffset(); // JS returns minutes WEST; flip sign

    const geoTry = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: false, maximumAge: 5 * 60_000, timeout: 6000 }
      );
    });

    (async () => {
      const coords = await geoTry();
      const params = coords
        ? { lat: coords.lat, lng: coords.lng, radius_km: 80, tz_offset, limit: 15 }
        : { tz_offset, limit: 15 };
      try {
        const { data } = await api.get("/businesses/popular-now", { params });
        if (cancelled) return;
        setState({ items: data.items || [], slot: data.slot, loading: false, nearMe: !!coords });
      } catch (e) {
        console.warn("popular-now failed:", e);
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scroll = (dir) => {
    railRef.current?.scrollBy({ left: dir * CARD_WIDTH * 2, behavior: "smooth" });
  };

  const Icon = useMemo(() => SLOT_ICONS[state.slot?.key] || Clock, [state.slot]);

  if (state.loading) return null;
  if (!state.items?.length || !state.slot) return null;

  return (
    <section className="mb-10" data-testid="popular-now-rail">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider bg-[#B31E1E] text-white shadow-sm">
            <Clock size={11} />
            Popular now
          </span>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tighter flex items-center gap-2" data-testid="popular-now-title">
            <Icon size={22} className="text-[#B31E1E]" />
            {state.slot.label}
          </h2>
          <span className="text-xs font-mono text-gray-500" data-testid="popular-now-hint">
            · {state.slot.hint}{state.nearMe ? " — near you" : ""}
          </span>
          <span className="text-xs font-mono text-gray-400">· {state.items.length} open</span>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => scroll(-1)}
            className="h-8 w-8 rounded-full border border-border bg-white hover:bg-gray-50 shadow-sm inline-flex items-center justify-center text-gray-600 hover:text-[#B31E1E]"
            aria-label="Scroll left"
            data-testid="popular-now-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            className="h-8 w-8 rounded-full border border-border bg-white hover:bg-gray-50 shadow-sm inline-flex items-center justify-center text-gray-600 hover:text-[#B31E1E]"
            aria-label="Scroll right"
            data-testid="popular-now-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={railRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
        data-testid="popular-now-rail-list"
      >
        {state.items.map((b) => {
          const hero = b.hero_image || b.logo_data_url || "";
          return (
            <Link
              key={b.id}
              to={`/businesses/${b.id}`}
              className="snap-start shrink-0 w-[220px] sm:w-[240px] rounded-xl border border-border bg-white overflow-hidden hover:shadow-md transition-shadow group"
              data-testid={`popular-now-card-${b.id}`}
            >
              <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                {hero ? (
                  <img src={hero} alt={b.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-4xl text-gray-300">
                    {(b.name || "?")[0]}
                  </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono bg-emerald-500/95 text-white shadow ring-1 ring-emerald-500/40">
                  Open now
                </span>
                {typeof b.distance_mi === "number" && (
                  <span className="absolute top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-mono bg-black/60 text-white">
                    {b.distance_mi < 0.1 ? "< 0.1 mi" : `${b.distance_mi.toFixed(1)} mi`}
                  </span>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-display text-base tracking-tight leading-tight truncate group-hover:text-[#B31E1E]" title={b.name}>
                  {b.name}
                </h3>
                <div className="mt-1 flex items-center gap-1.5">
                  <StarRating value={b.rating_avg || 0} size={12} />
                  <span className="text-[11px] font-mono tabular-nums text-gray-700">{(b.rating_avg || 0).toFixed(1)}</span>
                  <span className="text-[11px] font-mono text-gray-400">· {b.rating_count || 0}</span>
                </div>
                {(b.city || b.state || b.category) && (
                  <p className="mt-1 text-[11px] text-gray-500 inline-flex items-center gap-1 truncate">
                    <MapPin size={10} className="text-gray-400" />
                    <span className="truncate">{b.category || [b.city, b.state].filter(Boolean).join(", ")}</span>
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
